import axios from 'axios';
import { getValidToken } from '../contexts/AuthUtils';
import sourceUrl from './Source.api';
import { clearAppStorage } from '../utils/storageUtils';

let isRefreshing = false;
let failedQueue = [];

const getAdminDelegateToken = () => localStorage.getItem('adminDelegateToken');

const isLoginPage = () => {
  return window.location.pathname === '/login' || 
         window.location.pathname === '/signup' ||
         window.location.pathname === '/reset';
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleAuthFailure = () => {
  if (isLoginPage()) return;
  clearAppStorage();
  window.location.href = '/login';
};

const axiosInstance = axios.create({
  baseURL: sourceUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    const adminDelegateToken = getAdminDelegateToken();
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (adminDelegateToken) {
      config.headers['X-Admin-Token'] = `Bearer ${adminDelegateToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || '';
    const errorDetail = error.response?.data?.error?.message || '';

    const isAuthError = 
      status === 401 || 
      status === 403 ||
      (status === 400 && (
        errorMessage.toLowerCase().includes('expired token') ||
        errorDetail.toLowerCase().includes('token has expired')
      ));

    if (isAuthError && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await getValidToken(true);
        if (newToken) {
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
        throw new Error('Token refresh failed');
      } catch (refreshError) {
        processQueue(refreshError, null);
        handleAuthFailure();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
